import { type Station, stationStatusSchema } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  useToast,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Fuel, Plus, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { StationForm } from "../../../../features/station-form.js";
import { StationImport } from "../../../../features/station-import.js";
import { STATION_STATUS_TONE } from "../../../../features/station-status.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDate } from "../../../../lib/format.js";
import { useDocumentTitle } from "../../../../lib/hooks.js";
import { queryKeys } from "../../../../lib/query.js";
import { usePermissions } from "../../../../lib/session.js";
import { useTenantShape } from "../../../../lib/tenant-shape.js";

// Filtreler URL'de yaşar: bağlantı paylaşılabilir, geri tuşu beklendiği gibi çalışır. Bozuk değerler sessizce atılır.
const searchSchema = z.object({
  q: z.string().max(100).optional().catch(undefined),
  status: stationStatusSchema.optional().catch(undefined),
  companyId: z.string().uuid().optional().catch(undefined),
  regionId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/ayarlar/istasyonlar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: StationsPage,
});

const PAGE_SIZE = 25;

function StationsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const permissions = usePermissions();
  const shape = useTenantShape();
  useDocumentTitle(shape.isSingleStation ? t("menu.station") : t("menu.stations"));

  const [panel, setPanel] = useState<"create" | "import" | null>(null);
  const [searchText, setSearchText] = useState(filters.q ?? "");
  const pages = useCursorPages(JSON.stringify(filters));

  const setFilters = (patch: Partial<typeof filters>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  // Arama kutusu yazarken her tuşta sorgu atmaz; 300 ms duraklayınca URL güncellenir.
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((filters.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const query = {
    limit: PAGE_SIZE,
    ...(filters.q ? { search: filters.q } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.companyId ? { companyId: filters.companyId } : {}),
    ...(filters.regionId ? { regionId: filters.regionId } : {}),
  };
  const stations = useQuery({
    queryKey: queryKeys.stations({ ...query, cursor: pages.cursor ?? null }),
    queryFn: async () =>
      (
        await api.GET("/stations", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });
  const companies = useQuery({
    queryKey: queryKeys.companies,
    enabled: shape.showCompanySelector,
    queryFn: async () => (await api.GET("/companies")).data!.items,
  });
  const regions = useQuery({
    queryKey: queryKeys.regions,
    enabled: shape.showRegions,
    queryFn: async () => (await api.GET("/regions")).data!.items,
  });

  const columns = useMemo<DataTableColumn<Station>[]>(
    () => [
      {
        id: "code",
        header: t("stations.fields.code"),
        hideable: false,
        cell: (row) => <span className="font-mono font-medium">{row.code}</span>,
        exportValue: (row) => row.code,
      },
      {
        id: "name",
        header: t("stations.fields.name"),
        hideable: false,
        cell: (row) => <span className="font-medium">{row.name}</span>,
        exportValue: (row) => row.name,
      },
      ...(shape.showCompanySelector
        ? [
            {
              id: "company",
              header: t("stations.fields.company"),
              cell: (row: Station) => row.companyName,
              exportValue: (row: Station) => row.companyName,
            },
          ]
        : []),
      ...(shape.showRegions
        ? [
            {
              id: "region",
              header: t("stations.fields.region"),
              cell: (row: Station) => row.regionName ?? "—",
              exportValue: (row: Station) => row.regionName,
            },
          ]
        : []),
      {
        id: "city",
        header: t("common.city"),
        cell: (row) => [row.district, row.city].filter(Boolean).join(" / ") || "—",
        exportValue: (row) => [row.district, row.city].filter(Boolean).join(" / "),
      },
      {
        id: "distributor",
        header: t("stations.fields.distributor"),
        cell: (row) => row.distributorName ?? "—",
        exportValue: (row) => row.distributorName,
      },
      {
        id: "businessUnits",
        header: t("stations.businessUnits"),
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            {row.businessUnits
              .filter((unit) => unit.isActive)
              .map((unit) => (
                <StatusBadge key={unit.unitType}>{t(`enums.businessUnit.${unit.unitType}`)}</StatusBadge>
              ))}
          </div>
        ),
        exportValue: (row) =>
          row.businessUnits
            .filter((unit) => unit.isActive)
            .map((unit) => t(`enums.businessUnit.${unit.unitType}`))
            .join(", "),
      },
      {
        id: "epdkLicenseNo",
        header: t("stations.fields.epdkLicenseNo"),
        defaultVisible: false,
        cell: (row) => row.epdkLicenseNo ?? "—",
        exportValue: (row) => row.epdkLicenseNo,
      },
      {
        id: "epdkLicenseExpiresOn",
        header: t("stations.fields.epdkLicenseExpiresOn"),
        defaultVisible: false,
        cell: (row) => (row.epdkLicenseExpiresOn ? formatDate(row.epdkLicenseExpiresOn) : "—"),
        exportValue: (row) => (row.epdkLicenseExpiresOn ? formatDate(row.epdkLicenseExpiresOn) : null),
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={STATION_STATUS_TONE[row.status]}>
            {t(`enums.stationStatus.${row.status}`)}
          </StatusBadge>
        ),
        exportValue: (row) => t(`enums.stationStatus.${row.status}`),
      },
    ],
    [t, shape.showCompanySelector, shape.showRegions],
  );

  const hasFilters = Boolean(filters.q ?? filters.status ?? filters.companyId ?? filters.regionId);

  return (
    <>
      <PageHeader
        title={shape.isSingleStation ? t("menu.station") : t("menu.stations")}
        description={t("stations.description")}
        breadcrumb={[
          { label: t("menu.groups.settings") },
          { label: shape.isSingleStation ? t("menu.station") : t("menu.stations") },
        ]}
        actions={
          <>
            {permissions.has("stations.import") ? (
              <Button variant="outline" onClick={() => setPanel("import")}>
                <Upload aria-hidden="true" />
                {t("stations.import.action")}
              </Button>
            ) : null}
            {permissions.has("stations.manage") ? (
              <Button onClick={() => setPanel("create")}>
                <Plus aria-hidden="true" />
                {t("stations.add")}
              </Button>
            ) : null}
          </>
        }
      />

      <DataTable
        caption={t("menu.stations")}
        columns={columns}
        rows={stations.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={stations.isPending}
        error={stations.error ? <Alert tone="danger">{errorMessage(stations.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            icon={<Fuel />}
            title={hasFilters ? t("stations.empty.filteredTitle") : t("stations.empty.title")}
            description={
              hasFilters ? t("stations.empty.filteredDescription") : t("stations.empty.description")
            }
            {...(hasFilters
              ? {
                  action: (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchText("");
                        void navigate({ search: {}, replace: true });
                      }}
                    >
                      {t("common.clearFilters")}
                    </Button>
                  ),
                }
              : {})}
          />
        }
        filters={
          <>
            <FormField label={t("common.search")} className="w-56">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder={t("stations.searchPlaceholder")}
                />
              )}
            </FormField>
            <FormField label={t("common.status")} className="w-40">
              {(control) => (
                <Select
                  {...control}
                  value={filters.status ?? ""}
                  onChange={(event) =>
                    setFilters({ status: (event.target.value || undefined) as Station["status"] | undefined })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {stationStatusSchema.options.map((status) => (
                    <option key={status} value={status}>
                      {t(`enums.stationStatus.${status}`)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            {shape.showCompanySelector ? (
              <FormField label={t("stations.fields.company")} className="w-48">
                {(control) => (
                  <Select
                    {...control}
                    value={filters.companyId ?? ""}
                    onChange={(event) => setFilters({ companyId: event.target.value || undefined })}
                  >
                    <option value="">{t("common.all")}</option>
                    {(companies.data ?? []).map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.shortName ?? company.legalName}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            ) : null}
            {shape.showRegions ? (
              <FormField label={t("stations.fields.region")} className="w-44">
                {(control) => (
                  <Select
                    {...control}
                    value={filters.regionId ?? ""}
                    onChange={(event) => setFilters({ regionId: event.target.value || undefined })}
                  >
                    <option value="">{t("common.all")}</option>
                    {(regions.data ?? []).map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            ) : null}
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: stations.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(stations.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          // Dışa aktarma ekrandaki sayfayı değil, filtreye uyan TÜM kayıtları içerir.
          const rows = await fetchAllPages(
            async (cursor) =>
              (
                await api.GET("/stations", {
                  params: { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "istasyonlar",
            sheetName: t("menu.stations"),
            columns: visible,
            rows,
          });
        }}
        onRowClick={(row) =>
          void navigate({ to: "/ayarlar/istasyonlar/$stationId", params: { stationId: row.id } })
        }
      />

      <Drawer
        open={panel === "create"}
        onOpenChange={(open) => !open && setPanel(null)}
        title={t("stations.add")}
      >
        {panel === "create" ? (
          <StationForm
            onCancel={() => setPanel(null)}
            onSaved={(station) => {
              setPanel(null);
              toast({ tone: "success", title: t("stations.created", { name: station.name }) });
            }}
          />
        ) : null}
      </Drawer>
      <Dialog
        open={panel === "import"}
        onOpenChange={(open) => !open && setPanel(null)}
        title={t("stations.import.action")}
        className="max-w-3xl"
      >
        {panel === "import" ? (
          <StationImport
            onImported={(created) => {
              setPanel(null);
              toast({ tone: "success", title: t("stations.import.done", { count: created }) });
            }}
          />
        ) : null}
      </Dialog>
    </>
  );
}
